import { createElement, useCallback, useEffect, useRef } from 'react';
import * as ReactDOM from 'react-dom';
import { Map, Popup } from 'mapbox-gl';

import { useShowPointerOnHover } from 'modules/map';
import { ZoneLayers, ZoneLayersSources } from 'modules/mapLayers';
import EditControls from '../components/EditControls';
import { useCreateOrUpdateExclusionZone } from './useCreateOrUpdateExclusionZone';

export const useOnExclusionAreaClick = (
  map: Map,
  onEditClick: (value: number) => void,
  isEditing: boolean,
  isExclusionZoneVisibilityOn: boolean,
): void => {
  const popupRef = useRef<Popup | null>();
  useShowPointerOnHover(map, ZoneLayers.ExclusionZones);
  const hidePopup = useCallback(() => {
    if (popupRef?.current) {
      popupRef.current.remove();
    }
  }, []);
  const { handleUpdateOrCreate } = useCreateOrUpdateExclusionZone(hidePopup);
  const clickHandler = useCallback(
    (
      e: mapboxgl.MapMouseEvent & {
        features?: mapboxgl.MapboxGeoJSONFeature[] | undefined;
      } & mapboxgl.EventData,
    ) => {
      const features = map.queryRenderedFeatures(e.point);
      const isTopMostLayer = features[0].layer.id === ZoneLayers.ExclusionZones;
      if (isTopMostLayer && e.features && e.features[0]?.properties?.id) {
        const sourceFeatureId = e.features[0]?.id;
        setTimeout(() => {
          map.setFeatureState(
            {
              source: ZoneLayersSources.ExclusionZones,
              id: sourceFeatureId,
            },
            { clicked: true },
          );
        }, 100);
        const Controls = createElement(EditControls, {
          id: e.features[0]?.properties?.id,
          enabled: !!e.features[0]?.properties?.enabled,
          onEditClick,
          hidePopup,
          handleUpdateOrCreate,
        });
        const popupNode = document.createElement('div');
        ReactDOM.render(Controls, popupNode);
        const PopUp = new Popup({
          closeOnClick: true,
          closeButton: false,
          anchor: undefined,
        })
          .setLngLat(e.lngLat)
          .setDOMContent(popupNode)
          .addTo(map);
        PopUp.on('close', () => {
          map.removeFeatureState({
            source: ZoneLayersSources.ExclusionZones,
            id: sourceFeatureId,
          });
        });
        popupRef.current = PopUp;
      }
    },
    [map, onEditClick, handleUpdateOrCreate, hidePopup],
  );
  useEffect(() => {
    if (isEditing) {
      map.off('click', ZoneLayers.ExclusionZones, clickHandler);
    } else {
      map.on('click', ZoneLayers.ExclusionZones, clickHandler);
    }
    return (): void => {
      if (!isEditing) {
        map.off('click', ZoneLayers.ExclusionZones, clickHandler);
      }
    };
  }, [map, clickHandler, isEditing]);

  useEffect(() => {
    if (!isExclusionZoneVisibilityOn) {
      hidePopup();
    }
  }, [hidePopup, isExclusionZoneVisibilityOn]);
};
